import React, { useState } from 'react';
import styled from 'styled-components';

import {
  Popover,
  ButtonFilter,
  InputRadioGroup,
  Text,
  ButtonCondensed,
} from '@companycam/slab-web';

import { useModal } from 'components/shared/hooks';
import { translate } from 'javascripts/translations/i18n';
import { type ReviewProfile } from './hooks/useReviewProfiles';

type ReviewLocationsDropdownProps = {
  reviewProfiles: ReviewProfile[];
  selectedReviewProfile: ReviewProfile;
  onReviewProfileSelected: (profile: ReviewProfile) => void;
  allowReviewProfileAdd?: boolean;
  isInModal?: boolean;
};

const StyledPopover = styled(Popover)``;

const PopoverContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const LocationTextContainer = styled.div`
  padding-right: 12px;
`;

const LabelContainer = styled.div`
  min-width: 300px;
`;

const Footer = styled.div`
  display: flex;
  border-top: var(--cc_size_border_width_s) solid;
  border-color: var(--cc_color_border_default);
  padding: var(--cc_size_spacing_m) 0 var(--cc_size_spacing_xs)
    var(--cc_size_spacing_xs);
  margin-top: var(--cc_size_spacing_m);
`;

const ReviewProfilesDropdownList = ({
  reviewProfiles,
  selectedReviewProfile,
  onReviewProfileSelected,
  allowReviewProfileAdd = true,
  isInModal = false,
}: ReviewLocationsDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { showModal, Modal } = useModal();

  const getReviewProfileName = (profile: ReviewProfile) => {
    const reviewProfileName = 'Unnamed Location';

    if (profile.nickname) {
      return profile.nickname;
    }

    return reviewProfileName;
  };

  const reviewProfileName = getReviewProfileName(selectedReviewProfile);

  const handleAction = () => {
    setIsOpen((prevIsOpen) => {
      return !prevIsOpen;
    });
  };

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const profileSelected = reviewProfiles.find(
      (reviewProfile) => reviewProfile.id === parseInt(event.target.value),
    );
    onReviewProfileSelected(profileSelected);
    setIsOpen(false);
  };

  const handleAddLocationClick = () =>
    showModal(Modal.CREATE_REVIEW_PROFILE_MODAL);

  return (
    <StyledPopover
      isOpen={isOpen}
      onOpenChange={handleAction}
      mountNodeId={isInModal ? 'popover-in-modal-floating-ui' : undefined}
      placement="bottom-start"
      triggerButton={
        <ButtonFilter
          style={{ paddingRight: 12 }}
          icon={{
            name: 'chevron-down',
            position: 'after',
          }}
          status="normal"
        >
          <LocationTextContainer>{reviewProfileName}</LocationTextContainer>
        </ButtonFilter>
      }
    >
      <PopoverContent
        id={isInModal ? 'headlessui-menu-items-location-dropdown' : ''}
      >
        {reviewProfiles.length > 1 ? (
          <InputRadioGroup
            id="location-dropdown"
            label={null}
            hideLabel
            options={reviewProfiles.map((reviewProfile) => ({
              label: (
                <LabelContainer>
                  <Text as="p" family="heading">
                    {getReviewProfileName(reviewProfile)}
                  </Text>
                </LabelContainer>
              ),
              value: reviewProfile.id.toString(),
              style: {
                padding: 5,
              },
            }))}
            onChange={handleOnChange}
            selectedValue={selectedReviewProfile.id.toString()}
          />
        ) : (
          <LabelContainer>
            <Text as="p" family="heading">
              {reviewProfileName}
            </Text>
          </LabelContainer>
        )}
        {allowReviewProfileAdd ? (
          <Footer>
            <ButtonCondensed
              size="medium"
              onClick={handleAddLocationClick}
              data-testid="public-profile__locations-clear-filter"
              icon={{
                position: 'before',
                name: 'plus-circle-outline',
              }}
            >
              {translate('project.reviews.reviewsHub.addLocation')}
            </ButtonCondensed>
          </Footer>
        ) : null}
      </PopoverContent>
    </StyledPopover>
  );
};

export default ReviewProfilesDropdownList;
